import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import useApi from './useApi';

/**
 * Hook for fetching user roles from the API.
 *
 * Pattern follows OrcaWebsite's useGetRole:
 * - getUserRoles() is a simple function that triggers the API call
 * - A useEffect watches the state and processes the response
 * - This prevents infinite loops by not using useCallback with dependencies
 *   that change when roles are set
 */
const useGetUserRoles = () => {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const { setRoles, setCanEdit, setCanEditChemGuard, setIsLoadingRoles, setRolesVerified, loggedInUser } = useContext(AuthContext);
    const navigate = useNavigate();

    // Helper to build redirect URL with account info
    const buildLoginRedirectUrl = (error: string) => {
        const params = new URLSearchParams({ error });
        if (loggedInUser) {
            if (loggedInUser.fullName) params.set('name', loggedInUser.fullName);
            if (loggedInUser.userName) params.set('account', loggedInUser.userName);
        }
        return `/login?${params.toString()}`;
    };

    // Simple function to trigger the API call - no useCallback needed
    // This prevents infinite loops since the function reference doesn't change
    const getUserRoles = () => {
        if (!appSettings?.api) {
            return;
        }

        setIsLoadingRoles(true);
        setRolesVerified(false);

        getApiData({
            endpoint: `${appSettings.api}GetRoles`,
        });
    };

    // Process API response in a separate useEffect (like OrcaWebsite pattern)
    useEffect(() => {
        if (state.status === 'success' && state.result !== undefined) {
            // Parse roles array and set permissions
            if (Array.isArray(state.result)) {
                const rolesArray = state.result as string[];

                // Check if user has any roles assigned
                if (rolesArray.length === 0) {
                    // No roles - redirect to login with error message
                    setRoles([]);
                    setCanEdit(false);
                    setCanEditChemGuard(false);
                    setIsLoadingRoles(false);
                    setRolesVerified(true);
                    setTimeout(() => navigate(buildLoginRedirectUrl('no-roles')), 0);
                    return;
                }

                setRoles(rolesArray);
                setCanEdit(rolesArray.includes('CommodityBookEditor'));
                setCanEditChemGuard(rolesArray.includes('CommodityBookChemGuardEditor'));
            }

            setIsLoadingRoles(false);
            setRolesVerified(true);
        } else if (state.status === 'error') {
            // Check for 401 Unauthorized - redirect to login (like OrcaWebsite's useGetRole)
            if (state.error?.response?.status === 401) {
                console.error('[useGetUserRoles] 401 Unauthorized - redirecting to login');
                setRoles([]);
                setCanEdit(false);
                setCanEditChemGuard(false);
                setIsLoadingRoles(false);
                setRolesVerified(true);
                setTimeout(() => navigate(buildLoginRedirectUrl('unauthorized')), 0);
                return;
            }

            // For other errors, set fallback permissions
            console.error('[useGetUserRoles] Error fetching roles:', state.error);
            setRoles([]);
            setCanEdit(false);
            setCanEditChemGuard(false);
            setIsLoadingRoles(false);
            setRolesVerified(true);
        }
    }, [state, setRoles, setCanEdit, setCanEditChemGuard, setIsLoadingRoles, setRolesVerified, navigate]);

    // Parse permissions from current state for immediate access
    const canEdit = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookEditor')
        : false;
    const canEditChemGuard = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookChemGuardEditor')
        : false;

    return {
        state,
        canEdit,
        canEditChemGuard,
        getUserRoles,
    };
};

export default useGetUserRoles;
